import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  Api,
  ApiConfiguration,
  createDocumentClarification,
  exportDocumentMonth,
  getDocument,
  getDocumentMonths,
  getDocuments,
  getDocumentViewUrl,
  updateDocumentStatus,
} from '@taxora-api';
import {
  DocumentMonthSummaryDto,
  DocumentResponseDto,
  DocumentsListResponseDto,
} from '@taxora-shared-common/util';
import {
  CloseDrawer,
  CreateClarification,
  DownloadMonth,
  ExportMonth,
  LoadDocument,
  LoadDocuments,
  LoadMonths,
  OpenDrawer,
  ResetDocuments,
  UpdateStatus,
} from './documents.actions';

interface CachedViewUrl {
  url: string;
  cachedAt: number;
}

const VIEW_URL_TTL_MS = 50 * 60 * 1000; // 50 minutes

function monthKey(year: number | null, month: number | null): string {
  return `${year ?? 'null'}-${month ?? 'null'}`;
}

interface AccountantDocumentsStateModel {
  months: DocumentMonthSummaryDto[];
  documentsByMonth: Record<string, DocumentsListResponseDto>;
  loadingMonths: Record<string, boolean>;
  errorMonths: Record<string, boolean>;
  selectedDocument: DocumentResponseDto | null;
  drawerOpen: boolean;
  isLoadingMonths: boolean;
  isLoadingDocument: boolean;
  isUpdatingStatus: boolean;
  isSendingClarification: boolean;
}

@State<AccountantDocumentsStateModel>({
  name: 'accountantDocuments',
  defaults: {
    months: [],
    documentsByMonth: {},
    loadingMonths: {},
    errorMonths: {},
    selectedDocument: null,
    drawerOpen: false,
    isLoadingMonths: false,
    isLoadingDocument: false,
    isUpdatingStatus: false,
    isSendingClarification: false,
  },
})
@Injectable()
export class AccountantDocumentsState {
  private readonly api = inject(Api);
  private readonly apiConfig = inject(ApiConfiguration);

  private viewUrlCache = new Map<number, CachedViewUrl>();

  @Selector()
  static months(state: AccountantDocumentsStateModel): DocumentMonthSummaryDto[] {
    return state.months;
  }

  @Selector()
  static selectedDocument(state: AccountantDocumentsStateModel): DocumentResponseDto | null {
    return state.selectedDocument;
  }

  @Selector()
  static drawerOpen(state: AccountantDocumentsStateModel): boolean {
    return state.drawerOpen;
  }

  @Selector()
  static isLoadingMonths(state: AccountantDocumentsStateModel): boolean {
    return state.isLoadingMonths;
  }

  @Selector()
  static isLoadingDocument(state: AccountantDocumentsStateModel): boolean {
    return state.isLoadingDocument;
  }

  @Selector()
  static isUpdatingStatus(state: AccountantDocumentsStateModel): boolean {
    return state.isUpdatingStatus;
  }

  @Selector()
  static isSendingClarification(state: AccountantDocumentsStateModel): boolean {
    return state.isSendingClarification;
  }

  @Selector()
  static documentsByMonth(
    state: AccountantDocumentsStateModel,
  ): Record<string, DocumentsListResponseDto> {
    return state.documentsByMonth;
  }

  @Selector()
  static loadingMonths(state: AccountantDocumentsStateModel): Record<string, boolean> {
    return state.loadingMonths;
  }

  @Selector()
  static errorMonths(state: AccountantDocumentsStateModel): Record<string, boolean> {
    return state.errorMonths;
  }

  @Action(LoadMonths)
  async loadMonths(
    { patchState }: StateContext<AccountantDocumentsStateModel>,
    { relationshipId, organizationId }: LoadMonths,
  ): Promise<void> {
    patchState({ isLoadingMonths: true });
    try {
      const months = await this.api.invoke(getDocumentMonths, { relationshipId, organizationId });
      patchState({ months: months as unknown as DocumentMonthSummaryDto[] });
    } catch (error) {
      console.error('Failed to load document months', error);
    } finally {
      patchState({ isLoadingMonths: false });
    }
  }

  @Action(LoadDocuments)
  async loadDocuments(
    { getState, patchState }: StateContext<AccountantDocumentsStateModel>,
    { relationshipId, organizationId, year, month, filters }: LoadDocuments,
  ): Promise<void> {
    const key = monthKey(year, month);
    patchState({
      loadingMonths: { ...getState().loadingMonths, [key]: true },
      errorMonths: { ...getState().errorMonths, [key]: false },
    });
    try {
      const result = await this.api.invoke(getDocuments, {
        relationshipId,
        organizationId,
        year: year ?? undefined,
        month: month ?? undefined,
        // Backend filters `documentDate IS NULL` server-side for this bucket — without it,
        // omitting year/month returned the default (paginated, unfiltered) page of ALL
        // documents instead of just the "no document date" ones.
        noDocumentDate: year == null || month == null ? true : undefined,
        ...filters,
      });
      patchState({
        documentsByMonth: {
          ...getState().documentsByMonth,
          [key]: result as unknown as DocumentsListResponseDto,
        },
      });
    } catch (error) {
      console.error('Failed to load documents', error);
      patchState({ errorMonths: { ...getState().errorMonths, [key]: true } });
    } finally {
      patchState({ loadingMonths: { ...getState().loadingMonths, [key]: false } });
    }
  }

  @Action(LoadDocument)
  async loadDocument(
    { patchState }: StateContext<AccountantDocumentsStateModel>,
    { id }: LoadDocument,
  ): Promise<void> {
    patchState({ isLoadingDocument: true });
    try {
      const selectedDocument = await this.api.invoke(getDocument, { id });
      patchState({ selectedDocument: selectedDocument as unknown as DocumentResponseDto });
    } catch (error) {
      console.error('Failed to load document', error);
    } finally {
      patchState({ isLoadingDocument: false });
    }
  }

  @Action(UpdateStatus)
  async updateStatus(
    { getState, patchState }: StateContext<AccountantDocumentsStateModel>,
    { id, status }: UpdateStatus,
  ): Promise<void> {
    patchState({ isUpdatingStatus: true });
    try {
      const updated = await this.api.invoke(updateDocumentStatus, { id, body: { status } });
      if (getState().selectedDocument?.id === id) {
        patchState({ selectedDocument: updated as unknown as DocumentResponseDto });
      }
    } catch (error) {
      console.error('Failed to update document status', error);
      throw error;
    } finally {
      patchState({ isUpdatingStatus: false });
    }
  }

  @Action(CreateClarification)
  async createClarification(
    { patchState }: StateContext<AccountantDocumentsStateModel>,
    { id, message }: CreateClarification,
  ): Promise<void> {
    patchState({ isSendingClarification: true });
    try {
      const updated = await this.api.invoke(createDocumentClarification, { id, body: { message } });
      patchState({ selectedDocument: updated as unknown as DocumentResponseDto });
    } catch (error) {
      console.error('Failed to create clarification', error);
      throw error;
    } finally {
      patchState({ isSendingClarification: false });
    }
  }

  @Action(ExportMonth)
  async exportMonth(
    _ctx: StateContext<AccountantDocumentsStateModel>,
    { relationshipId, organizationId, year, month }: ExportMonth,
  ): Promise<void> {
    await this.api.invoke(exportDocumentMonth, {
      body: { relationshipId, organizationId, year, month },
    });
  }

  @Action(DownloadMonth)
  downloadMonth(
    _ctx: StateContext<AccountantDocumentsStateModel>,
    { relationshipId, organizationId, year, month }: DownloadMonth,
  ): void {
    const params = new URLSearchParams({
      relationshipId: String(relationshipId),
      organizationId: String(organizationId),
      year: String(year),
      month: String(month),
    });
    window.open(`${this.apiConfig.rootUrl}/documents/export/download?${params.toString()}`);
  }

  @Action(OpenDrawer)
  openDrawer(
    { patchState, dispatch }: StateContext<AccountantDocumentsStateModel>,
    { id }: OpenDrawer,
  ): void {
    patchState({ drawerOpen: true });
    void dispatch(new LoadDocument(id));
  }

  @Action(CloseDrawer)
  closeDrawer({ patchState }: StateContext<AccountantDocumentsStateModel>): void {
    patchState({ drawerOpen: false, selectedDocument: null });
  }

  @Action(ResetDocuments)
  reset({ setState }: StateContext<AccountantDocumentsStateModel>): void {
    this.viewUrlCache.clear();
    setState({
      months: [],
      documentsByMonth: {},
      loadingMonths: {},
      errorMonths: {},
      selectedDocument: null,
      drawerOpen: false,
      isLoadingMonths: false,
      isLoadingDocument: false,
      isUpdatingStatus: false,
      isSendingClarification: false,
    });
  }

  getCachedViewUrl(id: number): string | null {
    const cached = this.viewUrlCache.get(id);
    if (!cached) return null;
    if (Date.now() - cached.cachedAt > VIEW_URL_TTL_MS) {
      this.viewUrlCache.delete(id);
      return null;
    }
    return cached.url;
  }

  setCachedViewUrl(id: number, url: string): void {
    this.viewUrlCache.set(id, { url, cachedAt: Date.now() });
  }

  async getDocumentViewUrl(id: number): Promise<string | null> {
    const cached = this.getCachedViewUrl(id);
    if (cached) return cached;

    const response = await this.api.invoke(getDocumentViewUrl, { id });
    const url = response.url ?? null;
    if (url) {
      this.setCachedViewUrl(id, url);
    }
    return url;
  }
}
